// src/components/social_login/GoogleLoginButton.tsx
import { GoogleLogin } from "@react-oauth/google";
import { createData } from "api/apiHelpers";
import { useSocialAuth } from "../../hooks/useAuth";
import logger from "utils/logger";
import { Box } from "@mui/material";
import { useEffect, useRef, useState } from "react";

export default function GoogleLoginButton() {
  const { handleSocialAuthSuccess, handleSocialAuthError } = useSocialAuth();
  const containerRef = useRef<HTMLDivElement>(null);
  const [btnWidth, setBtnWidth] = useState(320);

  useEffect(() => {
    if (!containerRef.current) return;
    const ro = new ResizeObserver((entries) => {
      for (const entry of entries) {
        const w = Math.floor(entry.contentRect.width);
        if (w > 0) setBtnWidth(Math.min(w, 400));
      }
    });
    ro.observe(containerRef.current);
    return () => ro.disconnect();
  }, []);

  return (
    <Box
      ref={containerRef}
      sx={{
        width: "100%",
        height: 40,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        overflow: "hidden",
        borderRadius: "20px",
        "& iframe": { borderRadius: "20px", colorScheme: "light" },
        "& *:focus, & *:active, & *:focus-visible": {
          outline: "none !important",
          boxShadow: "none !important",
        },
      }}
    >
      <GoogleLogin
        onSuccess={async (credentialResponse) => {
          try {
            const id_token = credentialResponse?.credential;
            if (!id_token) {
              logger.error("No id_token from Google");
              return;
            }
            const data = await createData<{ access_token: string }, any>(
              "/auth/google/login/",
              { access_token: id_token }
            );
            logger.debug("GOOGLE LOGIN RESPONSE", data);

            await handleSocialAuthSuccess(data);
          } catch (err: unknown) {
            logger.error("Google login failed:", err);
            const axiosErr = err as { response?: { status?: number; data?: any } };
            if (axiosErr.response) {
              await handleSocialAuthError(
                "google",
                credentialResponse?.credential || "",
                { status: axiosErr.response.status } as Response,
                axiosErr.response.data,
              );
            }
          }
        }}
        onError={() => {
          logger.error("Google login popup failed");
        }}
        theme="outline"
        size="large"
        shape="pill"
        text="signin_with"
        logo_alignment="left"
        width={btnWidth}
        containerProps={{
          style: {
            width: "100%",
            display: "flex",
            justifyContent: "center",
          },
        }}
      />
    </Box>
  );
}
